import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Breadcrumbs,
  IdentityChipPrimitive,
  SearchPill,
  ThemeToggle
} from '../primitives';
import { useTheme } from '../../lib/theme';

export interface PenroseTopHeaderProps {
  /** Leaf crumb. When omitted, derived from the current route. */
  here?: ReactNode;
  /** Trailing scope crumb (typically the active org). */
  scope?: ReactNode;
  /** Search-pill click → opens command palette. */
  onSearchClick?: () => void;
  /** Identity chip display name. */
  identityName: string;
  /** Identity chip role label. */
  identityRole?: string;
  /** Identity chip click → opens flyout in the host shell. */
  onIdentityClick?: () => void;
  /** Flyout-open state for the identity chip. */
  identityExpanded?: boolean;
  /** Optional flyout — rendered inside `.pn-flyout-wrap` next to the chip so
   *  the absolute-positioned flyout anchors to it. The host shell controls
   *  mount/unmount + content. */
  identityFlyout?: ReactNode;
}

const ROUTE_LABELS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^\/dashboard\/?$/, 'Dashboard'],
  [/^\/projects\/?$/, 'Projects'],
  [/^\/projects\/[^/]+\/?$/, 'Projects'],
  [/^\/documents\/?$/, 'Documents'],
  [/^\/work-items\/?$/, 'Work Items'],
  [/^\/members\/?$/, 'Members'],
  [/^\/settings(\/|$)/, 'Settings'],
  [/^\/account\/?$/, 'Account'],
  [/^\/personal-todos\/?$/, 'Personal todos']
];

function labelForPath(pathname: string): string {
  for (const [pattern, label] of ROUTE_LABELS) {
    if (pattern.test(pathname)) return label;
  }
  return 'Penrose';
}

/**
 * Canonical signed-in top header — populates the `<PenroseShell header>` slot.
 * Composes shell primitives only (Breadcrumbs / SearchPill / ThemeToggle /
 * IdentityChipPrimitive). Connectivity lives in the identity flyout. Breadcrumbs derive from the current
 * route when `here` isn't supplied. Behavioral wiring (search palette,
 * diagnostics modal, identity flyout) is the host shell's responsibility —
 * this component is presentation + simple click forwarding.
 */
export function PenroseTopHeader({
  here,
  scope,
  onSearchClick,
  identityName,
  identityRole,
  onIdentityClick,
  identityExpanded,
  identityFlyout
}: PenroseTopHeaderProps) {
  const [theme, setTheme] = useTheme();
  const location = useLocation();
  const resolvedHere = here ?? labelForPath(location.pathname);

  const chip = (
    <IdentityChipPrimitive
      name={identityName}
      role={identityRole}
      expanded={identityExpanded}
      onClick={onIdentityClick}
      data-testid="identity-chip"
      data-flyout-trigger=""
    />
  );

  return (
    <>
      <Breadcrumbs here={resolvedHere} scope={scope} />
      <span className="pn-app-header-spacer" aria-hidden="true" />
      <SearchPill onClick={onSearchClick} aria-label="Search docs, tasks, members" />
      <ThemeToggle theme={theme} onChange={setTheme} />
      {identityFlyout != null ? (
        <div className="pn-flyout-wrap">
          {chip}
          {identityFlyout}
        </div>
      ) : (
        chip
      )}
    </>
  );
}
